import React, {
  useMemo,
  ElementType,
  forwardRef,
  useState,
  memo,
  MouseEvent,
  Ref,
  useCallback,
  RefObject,
} from 'react'
import { IconButtonProps } from '@mui/material'
import classNames from 'classnames'
import moment from 'moment'
import Button from 'transfix-ui/components/Button'
import { FormControl } from 'transfix-ui/components/FormControl'
import { useTextFieldStyles, ITextFieldProps } from 'transfix-ui/components/TextField'
import { makeStyles, TextField } from 'transfix-ui/core'
import {
  DatePicker as MuiDatePicker,
  DatePickerProps,
  MaterialUiPickersDate,
} from 'transfix-ui/core/pickers'
import { CalendarIcon } from 'transfix-icons'
import { TIconColor } from '../SvgIcon/types'
import { EndAdornment } from '../TextField/components/EndAdornment'
import { TDateFormat } from './types'
import { getParsedValue } from './utils/getParsedValue'

const getIconColor = ({
  disabled,
  invalid,
}: {
  disabled?: boolean
  invalid?: boolean
}): TIconColor => {
  if (invalid) {
    return 'error1'
  }

  if (disabled) {
    return 'coolGray5'
  }

  return 'skyBlue6'
}

const dayOfWeekFormatter: IDatePickerProps['dayOfWeekFormatter'] = day => {
  return day.slice(0, 3)
}

type TModalWrapperProp =
  | 'cancelLabel'
  | 'clearable'
  | 'clearLabel'
  | 'DialogProps'
  | 'okLabel'
  | 'showTodayButton'
  | 'todayLabel'

/**
 * These props are omitted and not customizable intentionally.
 * If you have a reason to change these, run it by the rest of the team.
 */
export type TOmittedProps =
  | TModalWrapperProp
  | 'disableToolbar'
  | 'variant'
  | 'TextFieldComponent'
  | 'onChange'

export type ParsableDate = object | string | number | Date | null | undefined

export interface IDatePickerProps<TDate extends TDateFormat = TDateFormat>
  extends Omit<DatePickerProps<TDate>, TOmittedProps>,
    Pick<ITextFieldProps, 'fullWidth' | 'size' | 'id'> {
  /**
   * Allows the input to be cleared
   * @default false
   */
  clearable?: boolean
  /**
   * Callback to be called when clear icon is clicked
   * @example '() => setValue(null)'
   */
  onClear?: () => void
  /**
   * Renders a field label if present
   */
  label?: string
  /**
   * @example 'This field is required'
   */
  validationMessage?: string
  /**
   * @example 'This field is required'
   */
  helperMessage?: string
  /**
   * The same as `error` prop
   */
  invalid?: boolean
  dataTest?: string
  required?: boolean
  error?: boolean
  /**
   * @example 'With min and max'
   */
  helperText?: string
  /**
   * @example '12/20/2024'
   */
  placeholder?: string
  /**
   * @example 'This field is required'
   */
  invalidDateMessage?: string
  onChange: (newVal: MaterialUiPickersDate) => void
}

const useOpenPickerButtonStyles = makeStyles({
  root: {
    padding: 4,
    minWidth: 'auto',
    marginLeft: -10,
    borderColor: 'transparent',
  },
})

const OpenPickerButton: ElementType<IconButtonProps> = memo(({ children, onClick }) => {
  const classes = useOpenPickerButtonStyles()
  return (
    <Button size='small' color='secondary' className={classes.root} onClick={onClick}>
      {children}
    </Button>
  )
})

const DatePickerTextField = (props: ITextFieldProps & { onClickClear: () => void }) => {
  const textFieldClasses = useTextFieldStyles(props)
  const { InputProps, className: inputClassName, clearable, large, onClickClear, value } =
    props || {}

  let val: any = value ?? null
  if (value === 'MM/DD/YYYY') {
    val = null
  }

  return (
    <TextField
      {...props}
      value={val}
      classes={{
        root: classNames(inputClassName, textFieldClasses.input, {
          [textFieldClasses.inputLarge]: large,
          [textFieldClasses.inputDatePickerMedium]: !large,
        }),
      }}
      InputProps={{
        ...InputProps,
        endAdornment: clearable ? (
          <EndAdornment dataTest='clear-icon' onClick={onClickClear} />
        ) : undefined,
      }}
    />
  )
}

function DatePickerComponent<TDate extends TDateFormat>(
  {
    clearable = false,
    disabled,
    error,
    fullWidth,
    helperMessage,
    invalid,
    label,
    onClear,
    required,
    format = 'MM/DD/YYYY',
    onChange: onChangeProp,
    readOnly: readOnlyProp = false,
    validationMessage,
    value: valueProp,
    views: viewsProp = ['day'],
    size = 'small',
    dataTest,
    minDate: minDateProp,
    maxDate: maxDateProp,
    ...props
  }: IDatePickerProps<TDate>,
  ref: Ref<HTMLDivElement> | undefined
) {
  const handleClickClear = useCallback(
    (e?: MouseEvent<HTMLButtonElement>) => {
      e?.stopPropagation()

      if (onClear) {
        onClear()
      }
    },
    [onClear]
  )

  const changeHandler = useCallback(
    (newVal: TDate | null) => {
      if (typeof newVal === 'undefined' || typeof newVal === 'number') {
        // nothing
      } else {
        onChangeProp(moment(newVal))
      }
    },
    [onChangeProp]
  )

  const [isOpen, setIsOpen] = useState<boolean>(false)

  const value = useMemo(() => getParsedValue(valueProp), [valueProp])
  const minDate = useMemo(() => getParsedValue(minDateProp), [minDateProp])
  const maxDate = useMemo(() => getParsedValue(maxDateProp), [maxDateProp])

  return (
    <FormControl
      validationMessage={validationMessage}
      helperMessage={helperMessage}
      disabled={disabled}
      error={invalid || error}>
      {label && (
        <label htmlFor='calendar' className={classNames({ required, label: true })}>
          {label}
        </label>
      )}
      <MuiDatePicker
        {...props}
        open={isOpen}
        format={format}
        value={value}
        readOnly={readOnlyProp}
        views={viewsProp}
        ref={ref}
        maxDate={maxDate as TDate}
        minDate={minDate as TDate}
        dayOfWeekFormatter={dayOfWeekFormatter}
        onChange={changeHandler}
        onClose={() => {
          setIsOpen(false)
        }}
        slots={{
          openPickerButton: OpenPickerButton,
          openPickerIcon: () => {
            const iconColor = getIconColor({ disabled, invalid: invalid || error })
            return <CalendarIcon size='large' color={iconColor} />
          },
          textField: props => (
            <DatePickerTextField
              {...props}
              data-test={dataTest}
              inputRef={props.inputRef as RefObject<HTMLInputElement> | undefined}
              onClickClear={handleClickClear}
              clearable={clearable}
              large={size === 'medium'}
              onFocus={() => setIsOpen(true)}
              size={size}
              error={error}
              disabled={disabled}
              fullWidth={fullWidth}
            />
          ),
        }}
        slotProps={{
          ...props.slotProps,
          inputAdornment: {
            position: 'start',
          },
          desktopTransition: {},
        }}
      />
    </FormControl>
  )
}

/**
 * @name DatePicker
 * @see https://material-ui-pickers.dev/demo/datepicker
 */
export const DatePicker = forwardRef<HTMLDivElement, IDatePickerProps>(DatePickerComponent)

export default DatePicker
